import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { calendarDayMonth, num } from '@lib/format';
import { useElementSize } from '@lib/hooks';

export type TrendTone = 'blue' | 'green' | 'amber' | 'ink';
export interface TrendSeries { key: string; label: string; values: number[]; tone: TrendTone; dashed?: boolean }

const M = { top: 8, right: 10, bottom: 24, left: 30 };

/** 0..max rounded up to 1, 2 or 5 x 10^n so the gridlines read as round numbers (at least 4: zeros stay calm). */
function niceMax(v: number) {
  if (v <= 4) return 4;
  const p = 10 ** Math.floor(Math.log10(v));
  const f = v / p;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * p;
}

/**
 * Line chart of a few daily series on one axis (pure SVG). Hover or arrow keys move a crosshair with every
 * series' value; a hidden table carries the same numbers for screen readers. `days` are YYYY-MM-DD.
 */
export function TrendChart({ days, series, label, height = 160 }: { days: string[]; series: TrendSeries[]; label: string; height?: number }) {
  const wrap = useRef<HTMLDivElement>(null);
  const size = useElementSize(wrap);
  const [active, setActive] = useState<number | null>(null);
  const n = days.length;
  const width = size?.width ?? 0;
  const iw = Math.max(0, width - M.left - M.right);
  const ih = height - M.top - M.bottom;
  const max = niceMax(Math.max(0, ...series.flatMap((s) => s.values)));
  const x = (i: number) => M.left + (n > 1 ? (i / (n - 1)) * iw : iw / 2);
  const y = (v: number) => M.top + ih - (v / max) * ih;
  const ticks = [0, max / 2, max];
  const labelled = n > 2 ? [0, Math.floor((n - 1) / 2), n - 1] : [...Array(n).keys()];

  function pick(e: PointerEvent<SVGSVGElement>) {
    if (!n || !iw) return;
    const r = e.currentTarget.getBoundingClientRect();
    // screen px -> the chart's own px (they differ under CSS zoom, e.g. the overview's monitor mode)
    const k = width ? r.width / width : 1;
    const i = Math.round((((e.clientX - r.left) / k - M.left) / iw) * (n - 1));
    setActive(Math.min(n - 1, Math.max(0, i)));
  }
  function onKey(e: KeyboardEvent<SVGSVGElement>) {
    const cur = active ?? n - 1;
    const next = { ArrowRight: cur + 1, ArrowLeft: cur - 1, Home: 0, End: n - 1 }[e.key];
    if (e.key === 'Escape') return setActive(null);
    if (next === undefined) return;
    e.preventDefault();
    setActive(Math.min(n - 1, Math.max(0, next)));
  }

  const tipLeft = active !== null ? Math.min(Math.max(x(active), 70), Math.max(70, width - 70)) : 0;
  return (
    <div className="trend">
      <ul className="trend-legend" aria-hidden="true">
        {series.map((s) => (
          <li key={s.key}>
            <i className={`trend-key series-${s.tone} ${s.dashed ? 'dashed' : ''}`} />
            {s.label}
          </li>
        ))}
      </ul>
      <div className="trend-plot" ref={wrap} style={{ height }}>
        {width > 0 && (
          <svg
            width={width}
            height={height}
            role="img"
            aria-label={`${label}. Gunakan panah kiri dan kanan untuk melihat angka per hari.`}
            tabIndex={0}
            onPointerMove={pick}
            onPointerLeave={() => setActive(null)}
            onKeyDown={onKey}
            onBlur={() => setActive(null)}
          >
            {ticks.map((t) => (
              <g key={t} className="trend-grid">
                <line x1={M.left} x2={M.left + iw} y1={y(t)} y2={y(t)} />
                <text x={M.left - 6} y={y(t)} dy="0.32em" textAnchor="end">{num(t)}</text>
              </g>
            ))}
            {labelled.map((i) => (
              <text key={i} className="trend-axis" x={x(i)} y={height - 6} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}>
                {calendarDayMonth(days[i])}
              </text>
            ))}
            {series.map((s) => (
              <path
                key={s.key}
                className={`trend-line series-${s.tone} ${s.dashed ? 'dashed' : ''}`}
                d={s.values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ')}
                pathLength={s.dashed ? undefined : 1}
              />
            ))}
            {active !== null && (
              <g className="trend-cross">
                <line x1={x(active)} x2={x(active)} y1={M.top} y2={M.top + ih} />
                {series.map((s) => (
                  <circle key={s.key} className={`series-${s.tone}`} cx={x(active)} cy={y(s.values[active] ?? 0)} r={4} />
                ))}
              </g>
            )}
          </svg>
        )}
        {active !== null && (
          <div className="trend-tip" style={{ left: tipLeft }} aria-hidden="true">
            <b>{calendarDayMonth(days[active])}</b>
            {series.map((s) => (
              <span key={s.key}>
                <i className={`trend-key series-${s.tone} ${s.dashed ? 'dashed' : ''}`} />
                <strong>{num(s.values[active] ?? 0)}</strong> {s.label}
              </span>
            ))}
          </div>
        )}
      </div>
      <table className="sr-only">
        <caption>{label}</caption>
        <thead>
          <tr>
            <th scope="col">Tanggal</th>
            {series.map((s) => <th key={s.key} scope="col">{s.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {days.map((d, i) => (
            <tr key={d}>
              <th scope="row">{calendarDayMonth(d)}</th>
              {series.map((s) => <td key={s.key}>{num(s.values[i] ?? 0)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
